import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useApiQuery } from '../../hooks/useApi';
import { useEmpresasContext } from '../../context/EmpresasContext';
import { formatDateSafe, formatDateTimeSafe } from '../../utils/dateUtils';
import {
  Button,
  Card,
  Badge,
  LoadingSpinner,
  ErrorState
} from '../../components/ui';
import { ReportesService, AuthService } from '../../services';
import ModalOpcionesPDF from '../../components/modales/ModalOpcionesPDF';
import ControlCombustibleReporteSection from '../../components/combustible/ControlCombustibleReporteSection';
import type { ReporteBackend } from '../../services/reportesService';

const ReporteDetalleView: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { usuario } = useAuth();
  const { empresaActiva } = useEmpresasContext();
  const navigate = useNavigate();
  const [modalPDFOpen, setModalPDFOpen] = useState(false);

  const reporteId = id ? parseInt(id, 10) : null;

  const {
    data: reporte,
    isLoading,
    error,
    refetch: refetchReporte  // 🆕 NUEVO: Función para forzar recarga
  } = useApiQuery(
    ['reporte', reporteId],
    () => reporteId ? ReportesService.getReporte(reporteId) : Promise.resolve(null),
    {
      enabled: !!reporteId,
      staleTime: 0,  // 🆕 NUEVO: No usar cache - siempre recargar
      cacheTime: 0,  // 🆕 NUEVO: No guardar en cache
    }
  );

  const puedeEditar = () => {
    if (!reporte || !usuario) return false;

    if (usuario.tipo_usuario === 'administrador') return true;
    if (usuario.tipo_usuario === 'supervisor') return true;
    if (usuario.tipo_usuario === 'operador') {
      return reporte.creado_por === usuario.id;
    }

    return false;
  };

  // Refrescar cuando se viene de edición
  useEffect(() => {
    // Verificar si se debe invalidar el cache al llegar a esta vista
    const shouldInvalidate = localStorage.getItem(`invalidate-reporte-${reporteId}`);
    if (shouldInvalidate && refetchReporte) {
      refetchReporte();
      localStorage.removeItem(`invalidate-reporte-${reporteId}`);
    }
  }, [reporteId, refetchReporte]);

  // Refrescar cuando la URL cambia (navegación)
  useEffect(() => {
    if (refetchReporte) {
      refetchReporte();
    }
  }, [reporteId]); // Se ejecuta cuando cambia el ID del reporte

  const handleEditar = () => {
    if (reporteId) {
      navigate(`/reportes/${reporteId}/editar`);
    }
  };

  const handleGenerarPDF = async (opciones: {
    tipo: 'frontend' | 'mapa' | 'filtros';
    incluirMapa: boolean;
    zoom: number;
  }) => {
    if (!reporte) return;

    try {
      let url = '';

      if (opciones.tipo === 'frontend') {
        url = `/mava/api/v1/reportes/${reporte.id}/pdf?include_map=${opciones.incluirMapa}&zoom=${opciones.zoom}`;
      } else if (opciones.tipo === 'mapa') {
        url = `/mava/api/v1/reportes/${reporte.id}/pdf-map?zoom=${opciones.zoom}`;
      }

      // Obtener token de autenticación usando AuthService
      const token = AuthService.getToken();
      if (!token) {
        throw new Error(t('errors.no_auth_token'));
      }

      // Hacer fetch con headers correctos
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Empresa-ID': empresaActiva?.empresa?.id?.toString() || '1', // Header requerido por el backend
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      // Convertir respuesta a blob
      const blob = await response.blob();

      // Crear enlace de descarga
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `reporte_${reporte.numero}_${new Date().toISOString().split('T')[0]}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpiar URL temporal
      URL.revokeObjectURL(link.href);

      toast.success(`PDF ${t('reports.downloaded_pdf')} ${reporte.numero}`);
      setModalPDFOpen(false);

    } catch (error: any) {
      console.error('Error generando PDF:', error);
      toast.error(`${t('errors.pdf_error')}: ${error.message}`);
    }
  };

  const getEstadoMaquinaColor = (estado: string) => {
    const colors = {
      'OPERATIVA': 'bg-green-100 text-green-800',
      'FALLA_MENOR': 'bg-yellow-100 text-yellow-800',
      'FALLA_MAYOR': 'bg-red-100 text-red-800',
      'MANTENIMIENTO': 'bg-blue-100 text-blue-800',
    };
    return colors[estado as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getEstadoColor = (estado: string) => {
    const colors = {
      'PENDIENTE': 'bg-yellow-100 text-yellow-800',
      'APROBADO': 'bg-green-100 text-green-800',
      'RECHAZADO': 'bg-red-100 text-red-800',
    };
    return colors[estado as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (!reporteId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <ErrorState
          message={t('errors.invalid_report_id')}
          actionText={t('reports.back_to_list')}
          onAction={() => navigate('/reportes')}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
          <span className="ml-2 text-gray-600">{t('reports.loading_report')}</span>
        </div>
      </div>
    );
  }

  if (error || !reporte) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <ErrorState
          message={t('errors.load_error')}
          actionText={t('reports.back_to_list')}
          onAction={() => navigate('/reportes')}
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('reports.report_number', { number: reporte.numero })}
          </h1>
          <p className="text-gray-600">
            {t('reports.created_by', { date: formatDateTimeSafe(reporte.created_at), user: reporte.creador_username })}
          </p>
          <div className="mt-2">
            <Badge className={getEstadoColor(reporte.estado)}>
              {reporte.estado}
            </Badge>
          </div>
        </div>

        <div className="flex space-x-2">
          <Button
            onClick={() => setModalPDFOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {t('reports.generate_pdf')}
          </Button>

          {puedeEditar() && (
            <Button
              onClick={handleEditar}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {t('common.edit')}
            </Button>
          )}

          <Button
            variant="outline"
            onClick={() => navigate('/reportes')}
          >
            {t('reports.back_to_list')}
          </Button>
        </div>
      </div>

      {/* Información General */}
      <Card>
        <Card.Header>
          <h3 className="text-lg font-semibold">{t('reports.general_info')}</h3>
        </Card.Header>
        <Card.Content>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('reports.machine')}</label>
              <div className="text-lg font-semibold text-gray-900">
                {reporte.maquina_codigo && reporte.maquina_descripcion && reporte.maquina_descripcion.trim()
                  ? `${reporte.maquina_codigo} - ${reporte.maquina_descripcion}`
                  : reporte.maquina_codigo || 'N/A'
                }
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('reports.operator')}</label>
              <div className="text-lg font-semibold text-gray-900">
                {reporte.operador_codigo ? `${reporte.operador_codigo} - ${reporte.operador_descripcion}` : t('reports.no_operator')}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('reports.date')}</label>
              <div className="text-lg font-semibold text-gray-900">
                {formatDateSafe(reporte.fecha)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('reports.machine_status')}</label>
              <Badge className={getEstadoMaquinaColor(reporte.estado_maquina)}>
                {reporte.estado_maquina}
              </Badge>
            </div>
          </div>
        </Card.Content>
      </Card>

      {/* Horómetros */}
      <Card>
        <Card.Header>
          <h3 className="text-lg font-semibold">{t('reports.horometers')}</h3>
        </Card.Header>
        <Card.Content>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {reporte.horometro_inicial}h
              </div>
              <div className="text-sm text-gray-500">{t('reports.initial')}</div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {reporte.horometro_final}h
              </div>
              <div className="text-sm text-gray-500">{t('reports.final')}</div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {reporte.horas_trabajadas}h
              </div>
              <div className="text-sm text-gray-500">{t('reports.worked')}</div>
            </div>
          </div>
        </Card.Content>
      </Card>

      {/* Faenas */}
      <Card>
        <Card.Header>
          <h3 className="text-lg font-semibold">{t('reports.faenas_done')}</h3>
        </Card.Header>
        <Card.Content>
          {reporte.faenas && reporte.faenas.length > 0 ? (
            <div className="space-y-4">
              {reporte.faenas.map((faena: any, index: number) => (
                <div key={faena.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        {t('reports.faena_number', { number: index + 1 })}: {faena.faena_codigo} - {faena.faena_descripcion}
                      </h4>
                      <div className="mt-2 text-sm text-gray-600">
                        <p>🏠 <strong>{t('admin.predios')}:</strong> {faena.predio_codigo} - {faena.predio_descripcion}</p>
                        <p>🌳 <strong>{t('admin.rodales')}:</strong> {faena.rodal_codigo} - {faena.rodal_descripcion}</p>
                        <p>⏱️ <strong>{t('reports.worked_hours')}:</strong> {faena.horas}h</p>
                        <p>📏 <strong>{t('common.area')}:</strong> {faena.area} ha</p>
                        {faena.archivos_kml && faena.archivos_kml.length > 0 && (
                          <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                            <p className="text-xs font-medium text-green-800 mb-1">
                              🗺️ {t('reports.kml_files')} ({faena.archivos_kml.length})
                            </p>
                            <div className="space-y-1">
                              {faena.archivos_kml.map((kml: any, kmlIndex: number) => (
                                <div key={kmlIndex} className="text-xs text-green-700">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium truncate" title={kml.nombre_archivo_original}>
                                      {kml.nombre_archivo_original}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-green-600 mt-1">
                                    {!!(kml.area_total_hectareas && kml.area_total_hectareas > 0) && (
                                      <span>📏 {kml.area_total_hectareas.toFixed(2)} ha</span>
                                    )}
                                    {!!(kml.cantidad_poligonos && kml.cantidad_poligonos > 0) && (
                                      <span>🔺 {kml.cantidad_poligonos} polígono{kml.cantidad_poligonos !== 1 ? 's' : ''}</span>
                                    )}
                                    {!!(kml.longitud_total_metros && parseFloat(kml.longitud_total_metros) > 0) && (
                                      <span>📐 {(parseFloat(kml.longitud_total_metros) / 1000).toFixed(2)} km</span>
                                    )}
                                    {!!(kml.cantidad_lineas && kml.cantidad_lineas > 0) && (
                                      <span>📏 {kml.cantidad_lineas} línea{kml.cantidad_lineas !== 1 ? 's' : ''}</span>
                                    )}
                                  </div>
                                  {!!kml.tiene_superposicion_con_existentes && (
                                    <div className="mt-1 p-1.5 bg-amber-50 border border-amber-200 rounded text-amber-800">
                                      <p className="font-medium">
                                        ⚠️ {t('kml.overlap_warning', {
                                          area: kml.superposicion_area_hectareas?.toFixed(2),
                                          percent: kml.superposicion_porcentaje?.toFixed(0),
                                        })}
                                      </p>
                                      {Array.isArray(kml.superposicion_detalle) && kml.superposicion_detalle.length > 0 && (
                                        <ul className="mt-0.5 ml-3 list-disc">
                                          {kml.superposicion_detalle.map((d: any, dIdx: number) => (
                                            <li key={dIdx} className="truncate">
                                              {d.nombre_archivo}
                                              {d.numero_reporte ? ` (${d.numero_reporte})` : ''}
                                              {d.predio_descripcion ? ` — ${d.predio_descripcion}` : ''}: {Number(d.area_hectareas).toFixed(2)} ha
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                              <div className="pt-1 border-t border-green-200 text-xs text-green-600 space-y-1">
                                {/* Área total KML - solo mostrar si hay área */}
                                {!!faena.archivos_kml.some((kml: any) => kml.area_total_hectareas && kml.area_total_hectareas > 0) && (
                                  <div className="flex items-center">
                                    <span>📊 {t('reports.total_kml_area')}: {faena.archivos_kml.reduce((total: number, kml: any) => total + (kml.area_total_hectareas || 0), 0).toFixed(2)} ha</span>
                                  </div>
                                )}
                                {/* Longitud total KML - solo mostrar si hay longitud */}
                                {!!faena.archivos_kml.some((kml: any) => kml.longitud_total_metros && parseFloat(kml.longitud_total_metros) > 0) && (
                                  <div className="flex items-center">
                                    <span>📐 {t('reports.total_kml_length')}: {(faena.archivos_kml.reduce((total: number, kml: any) => total + (parseFloat(kml.longitud_total_metros) || 0), 0) / 1000).toFixed(2)} km</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>{t('common.no_data')}</p>
            </div>
          )}
        </Card.Content>
      </Card>

      {/* Insumos */}
      <Card>
        <Card.Header>
          <h3 className="text-lg font-semibold">{t('reports.supplies_used')}</h3>
        </Card.Header>
        <Card.Content>
          {reporte.insumos && reporte.insumos.length > 0 ? (
            <div className="space-y-4">
              {reporte.insumos.map((insumo: any, index: number) => (
                <div key={insumo.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">
                        {t('admin.supplies')} {index + 1}: {insumo.insumo_descripcion}
                      </h4>
                      <div className="mt-2 text-sm text-gray-600">
                        <p>📦 <strong>{t('common.code')}:</strong> {insumo.insumo_codigo}</p>
                        <p>📊 <strong>{t('common.quantity')}:</strong> {insumo.cantidad} {insumo.unidad_medida}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>{t('common.no_data')}</p>
            </div>
          )}
        </Card.Content>
      </Card>

      {/* Mapa de Ubicaciones */}
      {reporte.faenas && reporte.faenas.length > 0 && reporte.tiene_ubicacion && (
        <Card>
          <Card.Header>
            <h3 className="text-lg font-semibold">{t('reports.location_map')}</h3>
          </Card.Header>
          <Card.Content>
            <div className="bg-gray-100 rounded-lg p-8 text-center">
              <p className="text-gray-600 mb-4">
                📍 {t('reports.map_faenas', { count: reporte.total_faenas })}
              </p>
              <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
                <span>{t('reports.total_area_worked')}: {reporte.total_area_trabajada} ha</span>
                {reporte.total_longitud_metros > 0 && (
                  <span>{t('reports.total_length')}: {(reporte.total_longitud_metros / 1000).toFixed(2)} km</span>
                )}
              </div>
              {/* TODO: Integrar componente de mapa cuando esté disponible */}
              <div className="mt-4 p-4 bg-white rounded border-2 border-dashed border-gray-300">
                <p className="text-gray-400 text-sm">
                  🗺️ {t('reports.map_pending')}
                </p>
              </div>
            </div>
          </Card.Content>
        </Card>
      )}

      {/* Observaciones */}
      {(reporte.observaciones || reporte.novedades) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {reporte.observaciones && (
            <Card>
              <Card.Header>
                <h3 className="text-lg font-semibold">{t('reports.observations')}</h3>
              </Card.Header>
              <Card.Content>
                <div className="text-gray-900 whitespace-pre-wrap">
                  {reporte.observaciones}
                </div>
              </Card.Content>
            </Card>
          )}

          {reporte.novedades && (
            <Card>
              <Card.Header>
                <h3 className="text-lg font-semibold">{t('reports.news')}</h3>
              </Card.Header>
              <Card.Content>
                <div className="text-gray-900 whitespace-pre-wrap">
                  {reporte.novedades}
                </div>
              </Card.Content>
            </Card>
          )}
        </div>
      )}

      {/* Metadatos */}
      <Card>
        <Card.Header>
          <h3 className="text-lg font-semibold">{t('reports.record_info')}</h3>
        </Card.Header>
        <Card.Content>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('reports.report_id')}</label>
              <div className="font-mono text-gray-900">#{reporte.id}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('reports.number')}</label>
              <div className="font-mono text-gray-900">{reporte.numero}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('reports.created_at')}</label>
              <div className="text-gray-900">
                {formatDateTimeSafe(reporte.created_at)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">{t('reports.updated_at')}</label>
              <div className="text-gray-900">
                {formatDateTimeSafe(reporte.updated_at)}
              </div>
            </div>
          </div>
        </Card.Content>
      </Card>

      {/* Control de combustible */}
      <ControlCombustibleReporteSection
        reporteId={reporte.id}
        nivelCombustibleInicio={reporte.nivel_combustible_inicio}
        nivelCombustibleFin={reporte.nivel_combustible_fin}
      />

      {/* Modal Opciones PDF */}
      <ModalOpcionesPDF
        isOpen={modalPDFOpen}
        reporte={reporte as ReporteBackend}
        onClose={() => setModalPDFOpen(false)}
        onGenerar={handleGenerarPDF}
      />
    </div>
  );
};

export default ReporteDetalleView;
